import React from "react"

import type { SxProps, Theme } from "@mui/material"
import { Box, Typography } from "@mui/material"
import { useGridRootProps } from "@mui/x-data-grid-pro"

const isFixedFilterRow = (
  node: HTMLElement | null,
  itemId: string
): boolean => {
  const row = node?.closest("[data-id]")

  return row instanceof HTMLElement && row.dataset.id === itemId
}

const assignHtmlElementRef = (
  ref: React.Ref<HTMLElement> | undefined,
  node: HTMLElement | null
): void => {
  if (typeof ref === "function") {
    ref(node)
    return
  }

  if (ref) {
    ref.current = node
  }
}

interface DataGridFixedLogicOperatorInputProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Filter item id whose row shows a readonly And. Other rows keep the
   * logic-operator select. Omit this when every row should show And.
   */
  itemId?: string
  native?: boolean
  ref?: React.Ref<HTMLElement>
  size?: "medium" | "small"
  sx?: SxProps<Theme>
  value?: unknown
}

export const DataGridFixedLogicOperatorInput = ({
  children,
  className,
  itemId,
  ref,
  sx,
  ...selectProps
}: DataGridFixedLogicOperatorInputProps) => {
  const { localeText, slots } = useGridRootProps()
  const [isFixedRow, setIsFixedRow] = React.useState(itemId == null)

  const assignRef = (node: HTMLElement | null) => {
    if (itemId != null) {
      setIsFixedRow(isFixedFilterRow(node, itemId))
    }
    assignHtmlElementRef(ref, node)
  }

  const SelectInput = slots.baseSelect as React.ElementType

  return (
    <Box className={className} ref={assignRef} sx={sx}>
      {isFixedRow ? (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "flex-start",
            pl: 1,
            width: "100%",
          }}
        >
          <Typography variant="body1">
            {localeText.filterPanelOperatorAnd}
          </Typography>
        </Box>
      ) : (
        <SelectInput {...selectProps}>{children}</SelectInput>
      )}
    </Box>
  )
}
